import {Hero} from '@primer/react-brand'
import {clsx} from 'clsx'
import {HeroCopilotHead} from './HeroCopilotHead'

import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'

import {AssetTypeEnum} from '../../types'
import {getBrandDitherColor} from '../../utils/brand-dither'
import {useDitherBackground} from '../../utils/useDitherBackground'
import {AssetVideo} from '../Video'
import type {FlexSuiteStackedHeroProps} from './types'
import {HeroContent} from './HeroContent'
import styles from './FlexSuiteHero.module.css'

/**
 * Stacked hero: center-aligned with a block-end image or video
 */
export function FlexSuiteHeroStacked({fields}: {fields: FlexSuiteStackedHeroProps}) {
  const {
    heroAsset,
    brand,
    showDither = false,
    options: {glassBorder, showCopilotHead},
  } = fields
  const {assetType, optionImage, optionVideo, optionPoster, autoplay, videoSource} = heroAsset?.fields ?? {}
  const {containerRef, ditherBackground} = useDitherBackground({
    className: styles.heroBg,
    enabled: showDither,
    params: {color: getBrandDitherColor(brand), type: 'wave', fadeIn: true},
  })
  const headingClassName = brand === 'Copilot' ? styles.heroCopilotHeading : undefined

  return (
    <>
      <HeroContent
        {...fields}
        data-testid="hero-stacked"
        imageContainerClassName={clsx(styles.heroImageWrapper, showDither && styles.withDither)}
        imageContainerRef={containerRef}
        headingClassName={headingClassName}
      >
        {assetType === AssetTypeEnum.enum.Image && optionImage && (
          <Hero.Image
            className={glassBorder ? styles.glassBorder : undefined}
            as="picture"
            src={`${optionImage.fields.file.url}?fm=webp`}
            sources={getImageSources(optionImage.fields.file.url, {
              maxWidth: MAX_CONTENT_WIDTH,
              disableDprScaling: true,
            })}
            alt={optionImage.fields.description ?? ''}
            enableBorder={false}
          />
        )}
        {assetType !== AssetTypeEnum.enum.Image && (optionVideo || videoSource) && (
          <Hero.Video className={glassBorder ? styles.glassBorder : undefined}>
            <AssetVideo
              assetType={assetType}
              optionVideo={optionVideo}
              optionPoster={optionPoster}
              autoplay={autoplay}
              videoSource={videoSource}
            />
          </Hero.Video>
        )}
      </HeroContent>
      {brand === 'Copilot' && showCopilotHead ? <HeroCopilotHead containerRef={containerRef} /> : null}
      {ditherBackground}
    </>
  )
}
